import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';

import Button from 'components/Button';
import Modal from 'components/Modal';
import FormInput from 'components/FormInput';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import logo from 'assets/images/logo.svg';
import API_ENDPOINTS from 'utils/api/endpoints';
import { toastStyle } from 'utils/helper';

import './styles.scss';

const tabs = [
  { key: 'settings', label: 'Budget Settings' },
  { key: 'locations', label: 'Locations' },
  { key: 'cast', label: 'Cast' },
  { key: 'crew', label: 'Crew' },
  { key: 'budget', label: 'Budget' },
];

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const numberFormatter = new Intl.NumberFormat('en-US');

const budgetTierFallbackOptions = [
  { label: 'Ultra Low', value: 0 },
  { label: 'Low', value: 1 },
  { label: 'Medium', value: 2 },
  { label: 'High', value: 3 },
];

const controlDefaults = {
  pages_per_day: 5,
  budget_tier: 1,
  primary_city: 0,
  sag_agreement: 0,
  crew_dept_head_day_rate: 850,
  crew_dept_head_day_rate_modified: false,
  location_base_day_rate: 2000,
  cast_day_rate: 255,
  pre_production_days: 45,
  prep_days: 3,
  wrap_days: 3,
  post_days: 45,
  production_design_percentage: 12,
  completion_bond_percentage: 2.5,
  contingency_percentage: 10,
  has_travel: true,
  has_fringes: true,
  cast_day_rate_modified: false,
};

const ScriptImportModal = () => {
  const {
    showScriptImport,
    hideScriptImportModal,
    createTabsMutation,
    makeAciveTabMutation,
    refetchTabs,
    refetchBudgets,
  } = useHomeScreenContext();

  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [budgetTitle, setBudgetTitle] = useState('');
  const [controlValues, setControlValues] = useState(controlDefaults);
  const [pendingControls, setPendingControls] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const latestAnalyzeRequest = useRef(0);
  const pendingControlsRef = useRef(null);

  useEffect(() => {
    return () => {
      if (fileUrl) URL.revokeObjectURL(fileUrl);
    };
  }, [fileUrl]);

  useEffect(() => {
    pendingControlsRef.current = pendingControls;
  }, [pendingControls]);

  const resetState = useCallback(() => {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFile(null);
    setFileUrl('');
    setAnalysis(null);
    setBudgetTitle('');
    setControlValues(controlDefaults);
    setPendingControls(null);
    setActiveTab('settings');
    setIsAnalyzing(false);
    setIsReanalyzing(false);
    setIsCreating(false);
  }, [fileUrl]);

  const handleClose = () => {
    if (isAnalyzing || isCreating) return;
    resetState();
    hideScriptImportModal();
  };

  const analyzeFile = useCallback(
    async (
      selectedFile,
      controls = null,
      { clearAnalysis = true, setInitialTitle = false } = {}
    ) => {
      const requestId = latestAnalyzeRequest.current + 1;
      latestAnalyzeRequest.current = requestId;

      const formData = new FormData();
      formData.append('file', selectedFile);
      const requestControlsKey = controls
        ? JSON.stringify(buildControlPayload(controls))
        : '';
      if (controls) {
        formData.append('options', requestControlsKey);
      }

      if (clearAnalysis) {
        setIsAnalyzing(true);
        setAnalysis(null);
      } else {
        setIsReanalyzing(true);
      }

      try {
        const response = await API_ENDPOINTS.budgets.analyze_script(formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (requestId !== latestAnalyzeRequest.current) return;

        const result = response?.data;
        const hasNewerPendingControls =
          controls &&
          pendingControlsRef.current &&
          JSON.stringify(buildControlPayload(pendingControlsRef.current)) !==
            requestControlsKey;
        setAnalysis(result);
        if (!hasNewerPendingControls) {
          setControlValues(previous =>
            extractControlValues(result, controls || previous)
          );
        }
        if (setInitialTitle) {
          setBudgetTitle(
            result?.script?.title ||
              selectedFile.name.replace(/\.pdf$/i, '') ||
              'Imported Script Budget'
          );
        }
      } catch (error) {
        if (requestId !== latestAnalyzeRequest.current) return;

        const message =
          error?.response?.data?.detail ||
          'Script Importer could not analyze this PDF.';
        toast.error(message, toastStyle);
      } finally {
        if (requestId === latestAnalyzeRequest.current) {
          setPendingControls(current => {
            if (!controls || !current) return null;
            const currentControlsKey = JSON.stringify(
              buildControlPayload(current)
            );
            return currentControlsKey === requestControlsKey ? null : current;
          });
          setIsAnalyzing(false);
          setIsReanalyzing(false);
        }
      }
    },
    []
  );

  const onDrop = useCallback(
    acceptedFiles => {
      const selectedFile = acceptedFiles?.[0];
      if (!selectedFile) return;

      if (fileUrl) URL.revokeObjectURL(fileUrl);
      setFile(selectedFile);
      setFileUrl(URL.createObjectURL(selectedFile));
      setControlValues(controlDefaults);
      setPendingControls(null);
      analyzeFile(selectedFile, null, { setInitialTitle: true });
    },
    [analyzeFile, fileUrl]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    noClick: Boolean(file),
    noKeyboard: Boolean(file),
    onDrop,
  });

  useEffect(() => {
    if (!file || !analysis || !pendingControls) return undefined;

    const timeoutId = setTimeout(() => {
      analyzeFile(file, pendingControls, { clearAnalysis: false });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [analysis, analyzeFile, file, pendingControls]);

  const handleControlChange = useCallback(
    (key, value) => {
      const nextControls = {
        ...controlValues,
        [key]: value,
      };

      if (key === 'cast_day_rate') {
        nextControls.cast_day_rate_modified = true;
      }
      if (key === 'crew_dept_head_day_rate') {
        nextControls.crew_dept_head_day_rate_modified = true;
      }
      if (key === 'sag_agreement') {
        nextControls.cast_day_rate_modified = false;
      }

      setControlValues(nextControls);
      setPendingControls(nextControls);
    },
    [controlValues]
  );

  const topStats = useMemo(() => {
    return [
      ['# of Crew', analysis?.counts?.crew],
      ['# of Cast', analysis?.counts?.cast],
      ['Shooting Days', analysis?.counts?.shooting_days],
      ['Budget Total', analysis?.totals?.grand_total, 'currency'],
    ];
  }, [analysis]);

  const handleCreate = async () => {
    if (!analysis || !budgetTitle.trim()) return;

    setIsCreating(true);
    try {
      const response = await API_ENDPOINTS.budgets.create_budget_from_script({
        name: budgetTitle.trim(),
        analysis,
      });
      const newBudget = response?.data;
      if (newBudget?.id) {
        const tabResponse = await createTabsMutation.mutateAsync([
          { budget: newBudget.id },
        ]);
        const tab = tabResponse?.data?.[0];
        if (tab?.id) {
          await makeAciveTabMutation.mutateAsync(tab.id);
        }
      }
      await refetchTabs?.();
      await refetchBudgets?.();
      toast.success(
        `Budget "${newBudget?.name || budgetTitle}" created.`,
        toastStyle
      );
      resetState();
      hideScriptImportModal();
      setTimeout(() => window.location.reload(), 100);
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        'Could not create a budget from this script.';
      toast.error(message, toastStyle);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal
      show={showScriptImport}
      onHide={handleClose}
      size="xl"
      className="script-import-modal"
      bodyClass="script-import-modal__body"
      centered
    >
      <div className="script-import">
        <section className="script-import__content">
          <header className="script-import__header">
            <div className="script-import__title">
              <img src={logo} alt="CineBudget Pro" />
              <h1>Create Budget from Script</h1>
            </div>
            <div className="script-import__stats">
              {topStats.map(([label, value, type]) => (
                <span key={label}>
                  {label}: <strong>{formatValue(value, type)}</strong>
                </span>
              ))}
            </div>
            <button
              type="button"
              className="script-import__close"
              onClick={handleClose}
              aria-label="Close"
            >
              x
            </button>
          </header>

          <div className="script-import__main">
            <div className="script-import__preview" {...getRootProps()}>
              <input {...getInputProps()} />
              {fileUrl ? (
                <>
                  <div className="script-import__pdf-toolbar">
                    <span>{file?.name}</span>
                    <Button
                      label="Replace"
                      type="button"
                      variant="secondary"
                      onClick={open}
                    />
                  </div>
                  <iframe src={fileUrl} title="Screenplay preview" />
                </>
              ) : (
                <div className="script-import__dropzone">
                  <h2>
                    {isDragActive
                      ? 'Drop the screenplay here'
                      : 'Drop screenplay PDF'}
                  </h2>
                  <p>or click to choose a file</p>
                </div>
              )}
            </div>

            <div className="script-import__panel">
              <div className="script-import__file-summary">
                <span>
                  Script File:{' '}
                  <strong>
                    {analysis?.script?.file_name || file?.name || '-'}
                  </strong>
                </span>
                <span>
                  Pages: <strong>{analysis?.script?.pages || '-'}</strong>
                </span>
                <span>
                  Words:{' '}
                  <strong>
                    {analysis?.script?.words
                      ? numberFormatter.format(analysis.script.words)
                      : '-'}
                  </strong>
                </span>
              </div>

              <div className="script-import__title-field">
                <FormInput
                  label="Budget Title"
                  type="text"
                  value={budgetTitle}
                  placeholder="Please enter a budget title"
                  onChange={event => setBudgetTitle(event.target.value)}
                />
              </div>

              <div className="script-import__tabs">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    className={activeTab === tab.key ? 'active' : ''}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
                {isReanalyzing ? (
                  <span className="script-import__tab-status">
                    Updating totals...
                  </span>
                ) : null}
              </div>

              <div className="script-import__tab-content">
                {isAnalyzing && !analysis ? (
                  <div className="script-import__loading">
                    Analyzing script...
                  </div>
                ) : (
                  renderTab(
                    activeTab,
                    analysis,
                    controlValues,
                    handleControlChange
                  )
                )}
              </div>

              <footer className="script-import__actions">
                <Button
                  label="Cancel"
                  type="button"
                  variant="red-outlined"
                  onClick={handleClose}
                  isDisabled={isAnalyzing || isCreating}
                />
                <Button
                  label={isCreating ? 'Creating...' : 'Create'}
                  type="button"
                  onClick={handleCreate}
                  isDisabled={
                    !analysis ||
                    !budgetTitle.trim() ||
                    isAnalyzing ||
                    isCreating
                  }
                />
              </footer>
            </div>
          </div>
        </section>
      </div>
    </Modal>
  );
};

const renderTab = (activeTab, analysis, controlValues, onControlChange) => {
  if (!analysis) {
    return (
      <div className="script-import__empty">
        Upload a screenplay PDF to generate the budget draft.
      </div>
    );
  }

  if (activeTab === 'locations')
    return <LocationsTab locations={analysis.locations} />;
  if (activeTab === 'cast') return <CastTab roles={analysis.roles} />;
  if (activeTab === 'crew') return <CrewTab crew={analysis.crew} />;
  if (activeTab === 'budget')
    return <BudgetTab categories={analysis.budget?.categories} />;
  return (
    <SettingsTab
      analysis={analysis}
      controls={controlValues}
      onControlChange={onControlChange}
    />
  );
};

const SettingsTab = ({ analysis, controls, onControlChange }) => {
  const options = analysis?.options || {};
  const budgetTierOptions = options.budget_tiers?.length
    ? options.budget_tiers
    : budgetTierFallbackOptions;
  const cityOptions = options.primary_cities || [];
  const sagOptions = options.sag_agreements || [];

  return (
    <div className="script-import__settings-layout">
      <section className="script-import__settings-section">
        <h2>Production Information</h2>
        <div className="script-import__control-card">
          <SelectControl
            label="Primary City"
            value={controls.primary_city}
            options={cityOptions}
            onChange={value => onControlChange('primary_city', value)}
          />
          <RangeControl
            label="Pages per Day"
            value={controls.pages_per_day}
            min={2}
            max={22}
            onChange={value => onControlChange('pages_per_day', value)}
          />
        </div>
      </section>

      <section className="script-import__settings-section">
        <h2>Budget Tier</h2>
        <SegmentedControl
          value={controls.budget_tier}
          options={budgetTierOptions}
          onChange={value => onControlChange('budget_tier', value)}
        />
      </section>

      <section className="script-import__settings-section">
        <h2>Rates</h2>
        <div className="script-import__control-card">
          <StepperControl
            label="Crew Dept Head Day Rate"
            value={controls.crew_dept_head_day_rate}
            min={0}
            step={50}
            type="currency"
            onChange={value =>
              onControlChange('crew_dept_head_day_rate', value)
            }
          />
          <StepperControl
            label="Location Base Day Rate"
            value={controls.location_base_day_rate}
            min={0}
            step={100}
            type="currency"
            onChange={value => onControlChange('location_base_day_rate', value)}
          />
          <StepperControl
            label="Prepro Days"
            value={controls.pre_production_days}
            min={0}
            step={1}
            onChange={value => onControlChange('pre_production_days', value)}
          />
          <StepperControl
            label="Prep Days"
            value={controls.prep_days}
            min={0}
            step={1}
            onChange={value => onControlChange('prep_days', value)}
          />
          <StepperControl
            label="Wrap Days"
            value={controls.wrap_days}
            min={0}
            step={1}
            onChange={value => onControlChange('wrap_days', value)}
          />
          <StepperControl
            label="Post Days"
            value={controls.post_days}
            min={0}
            step={1}
            onChange={value => onControlChange('post_days', value)}
          />
          <StepperControl
            label="Prod. Design"
            value={controls.production_design_percentage}
            min={0}
            max={100}
            step={0.5}
            type="percent"
            onChange={value =>
              onControlChange('production_design_percentage', value)
            }
          />
          <StepperControl
            label="Completion Bond"
            value={controls.completion_bond_percentage}
            min={0}
            max={5}
            step={0.25}
            type="percent"
            onChange={value =>
              onControlChange('completion_bond_percentage', value)
            }
          />
          <StepperControl
            label="Contingency"
            value={controls.contingency_percentage}
            min={0}
            max={25}
            step={0.5}
            type="percent"
            onChange={value => onControlChange('contingency_percentage', value)}
          />
          <ToggleControl
            label="Has Travel"
            checked={controls.has_travel}
            onChange={value => onControlChange('has_travel', value)}
          />
        </div>
      </section>

      <section className="script-import__settings-section">
        <h2>Cast Settings</h2>
        <div className="script-import__control-card">
          <SelectControl
            label="SAG"
            value={controls.sag_agreement}
            options={sagOptions}
            onChange={value => onControlChange('sag_agreement', value)}
          />
          <StepperControl
            label="Cast Day Rate"
            value={controls.cast_day_rate}
            min={0}
            step={25}
            type="currency"
            onChange={value => onControlChange('cast_day_rate', value)}
          />
        </div>
      </section>
    </div>
  );
};

SettingsTab.propTypes = {
  analysis: PropTypes.object.isRequired,
  controls: PropTypes.object.isRequired,
  onControlChange: PropTypes.func.isRequired,
};

const SelectControl = ({ label, value, options, onChange }) => (
  <label className="script-import__field-row">
    <span>{label}</span>
    <select
      value={value ?? ''}
      onChange={event => onChange(Number(event.target.value))}
      disabled={!options.length}
    >
      {options.length ? null : <option value="">-</option>}
      {options.map(option => (
        <option key={`${label}-${option.value}`} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

SelectControl.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  options: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

SelectControl.defaultProps = {
  value: null,
  options: [],
};

const RangeControl = ({ label, value, min, max, onChange }) => (
  <div className="script-import__range-control">
    <div className="script-import__range-heading">
      <span>{label}</span>
      <strong>{numberFormatter.format(value || 0)}</strong>
    </div>
    <div className="script-import__range-track">
      <span>{min}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={event => onChange(Number(event.target.value))}
      />
      <span>{max}</span>
    </div>
  </div>
);

RangeControl.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

const SegmentedControl = ({ value, options, onChange }) => (
  <div className="script-import__segmented-control">
    {options.map(option => (
      <button
        key={option.value}
        type="button"
        className={Number(value) === Number(option.value) ? 'active' : ''}
        onClick={() => onChange(Number(option.value))}
      >
        {option.label}
      </button>
    ))}
  </div>
);

SegmentedControl.propTypes = {
  value: PropTypes.number.isRequired,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

const StepperControl = ({ label, value, min, max, step, type, onChange }) => {
  const handleStep = direction => {
    const nextValue = roundControlValue(
      clamp(Number(value || 0) + step * direction, min, max)
    );
    onChange(nextValue);
  };

  return (
    <div className="script-import__stepper-row">
      <span>{label}</span>
      <div className="script-import__stepper">
        <button
          type="button"
          onClick={() => handleStep(-1)}
          aria-label={`Decrease ${label}`}
        >
          -
        </button>
        <strong>{formatControlValue(value, type)}</strong>
        <button
          type="button"
          onClick={() => handleStep(1)}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
};

StepperControl.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  type: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

StepperControl.defaultProps = {
  min: 0,
  max: null,
  step: 1,
  type: 'number',
};

const ToggleControl = ({ label, checked, onChange }) => (
  <label className="script-import__toggle-row">
    <span>{label}</span>
    <input
      type="checkbox"
      checked={Boolean(checked)}
      onChange={event => onChange(event.target.checked)}
    />
  </label>
);

ToggleControl.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const LocationsTab = ({ locations = [] }) => (
  <table className="script-import__table">
    <thead>
      <tr>
        <th>Location Name</th>
        <th>Pages</th>
      </tr>
    </thead>
    <tbody>
      {locations.map(location => (
        <tr key={location.id || location.name}>
          <td>{location.name}</td>
          <td>{numberFormatter.format(location.page_count || 0)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

LocationsTab.propTypes = {
  locations: PropTypes.array,
};

LocationsTab.defaultProps = {
  locations: [],
};

const CastTab = ({ roles = [] }) => (
  <table className="script-import__table">
    <thead>
      <tr>
        <th>Role</th>
        <th>Lines</th>
        <th>Pages</th>
      </tr>
    </thead>
    <tbody>
      {roles.map(role => (
        <tr key={role.id || role.title}>
          <td>{role.title}</td>
          <td>{numberFormatter.format(role.lines || 0)}</td>
          <td>{numberFormatter.format(role.pages || 0)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

CastTab.propTypes = {
  roles: PropTypes.array,
};

CastTab.defaultProps = {
  roles: [],
};

const CrewTab = ({ crew = [] }) => (
  <table className="script-import__table">
    <thead>
      <tr>
        <th>Role</th>
      </tr>
    </thead>
    <tbody>
      {crew.map(member => (
        <tr key={member.id || member.name}>
          <td>{member.name}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

CrewTab.propTypes = {
  crew: PropTypes.array,
};

CrewTab.defaultProps = {
  crew: [],
};

const BudgetTab = ({ categories }) => {
  const categoryList = categories?.categories || [];
  return (
    <table className="script-import__table">
      <thead>
        <tr>
          <th>Section</th>
          <th>Sub-Total</th>
        </tr>
      </thead>
      <tbody>
        {categoryList.map(category => (
          <tr key={category.name}>
            <td>{category.name}</td>
            <td>{formatValue(category.total, 'currency')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

BudgetTab.propTypes = {
  categories: PropTypes.object,
};

BudgetTab.defaultProps = {
  categories: null,
};

const formatValue = (value, type) => {
  if (value === undefined || value === null || value === '') return '-';
  if (type === 'currency') return currencyFormatter.format(Number(value || 0));
  if (type === 'percent') return `${Number(value || 0).toFixed(2)}%`;
  return numberFormatter.format(Number(value || 0));
};

const formatControlValue = (value, type) => {
  if (type === 'currency') return currencyFormatter.format(Number(value || 0));
  if (type === 'percent') return `${Number(value || 0).toFixed(2)}%`;
  return numberFormatter.format(Number(value || 0));
};

const extractControlValues = (analysis, previous = controlDefaults) => {
  const settings = analysis?.settings || {};

  return {
    pages_per_day: numberOrDefault(
      settings.pages_per_day,
      previous.pages_per_day
    ),
    budget_tier: numberOrDefault(settings.budget_tier, previous.budget_tier),
    primary_city: numberOrDefault(settings.primary_city, previous.primary_city),
    sag_agreement: numberOrDefault(
      settings.sag_agreement,
      previous.sag_agreement
    ),
    crew_dept_head_day_rate: numberOrDefault(
      settings.crew_dept_head_day_rate,
      previous.crew_dept_head_day_rate
    ),
    crew_dept_head_day_rate_modified: Boolean(
      settings.has_crew_dept_head_day_rate_override
    ),
    location_base_day_rate: numberOrDefault(
      settings.location_base_day_rate,
      previous.location_base_day_rate
    ),
    cast_day_rate: numberOrDefault(
      settings.cast_day_rate,
      previous.cast_day_rate
    ),
    pre_production_days: numberOrDefault(
      settings.pre_production_days,
      previous.pre_production_days
    ),
    prep_days: numberOrDefault(settings.prep_days, previous.prep_days),
    wrap_days: numberOrDefault(settings.wrap_days, previous.wrap_days),
    post_days: numberOrDefault(settings.post_days, previous.post_days),
    production_design_percentage: numberOrDefault(
      settings.production_design_percentage,
      previous.production_design_percentage
    ),
    completion_bond_percentage: numberOrDefault(
      settings.completion_bond_percentage,
      previous.completion_bond_percentage
    ),
    contingency_percentage: numberOrDefault(
      settings.contingency_percentage,
      previous.contingency_percentage
    ),
    has_travel: booleanOrDefault(settings.has_travel, previous.has_travel),
    has_fringes: booleanOrDefault(settings.has_fringes, previous.has_fringes),
    cast_day_rate_modified: Boolean(settings.has_cast_day_rate_override),
  };
};

const buildControlPayload = controls => {
  const payload = { ...controls };
  if (!payload.cast_day_rate_modified) {
    delete payload.cast_day_rate;
  }
  if (!payload.crew_dept_head_day_rate_modified) {
    delete payload.crew_dept_head_day_rate;
  }
  delete payload.cast_day_rate_modified;
  delete payload.crew_dept_head_day_rate_modified;
  return payload;
};

const numberOrDefault = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const booleanOrDefault = (value, fallback) => {
  return value === undefined || value === null ? fallback : Boolean(value);
};

const clamp = (value, min, max) => {
  const minClamped = Math.max(value, min);
  return max === null || max === undefined
    ? minClamped
    : Math.min(minClamped, max);
};

const roundControlValue = value => Math.round(value * 100) / 100;

export default ScriptImportModal;
